import { motion } from "framer-motion";
import { Mail, Phone, MapPin, Instagram, Facebook } from "lucide-react";
import { BRAND } from "../lib/data";
import { useT } from "../lib/i18n";
import { MustacheMark } from "./MustacheMark";
import ContactForm from "./ContactForm";

const TikTokIcon = ({ size = 16 }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M9 12a4 4 0 1 0 4 4V4a5 5 0 0 0 5 5" />
  </svg>
);

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.22, 1, 0.36, 1] } },
};

export const FindUs = () => {
  const { t, lang } = useT();
  return (
    <section
      id="find"
      data-testid="find-section"
      className="relative bg-black text-white py-24 md:py-40"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <motion.div
          variants={{ hidden: {}, show: { transition: { staggerChildren: 0.1 } } }}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-15%" }}
          className="grid grid-cols-12 gap-10 md:gap-16"
        >
          <div className="col-span-12 md:col-span-5">
            <motion.span
              variants={fadeUp}
              className="text-[11px] tracking-[0.4em] uppercase text-gold"
            >
              {t("find.kicker")}
            </motion.span>
            <motion.h2
              variants={fadeUp}
              data-testid="find-heading"
              className="mt-4 font-display text-4xl md:text-6xl leading-[1.02] tracking-tight"
            >
              {t("find.heading.1")}{" "}
              <span className="italic text-gold">{t("find.heading.bonjour")}</span>.
            </motion.h2>
            <motion.p
              variants={fadeUp}
              className="mt-6 text-white/60 font-light leading-relaxed max-w-md"
            >
              {t("find.body")}
            </motion.p>
            <motion.div variants={fadeUp} className="mt-10 flex items-center gap-4">
              <MustacheMark className="h-5 w-auto" color="#D4A857" />
              <span className="text-[10px] tracking-[0.4em] uppercase text-gold/80">
                {t("find.badge")}
              </span>
            </motion.div>
          </div>

          <div className="col-span-12 md:col-span-7">
            <motion.div
              variants={fadeUp}
              className="grid grid-cols-1 sm:grid-cols-2 gap-px bg-gold/15 ring-1 ring-gold/15"
            >
              <a
                data-testid="contact-email"
                href={`mailto:${BRAND.email}`}
                className="group p-8 md:p-10 bg-black hover:bg-[#0a0a0a] transition-colors flex flex-col gap-4"
              >
                <Mail size={18} className="text-gold" />
                <div>
                  <div className="text-[10px] tracking-[0.35em] uppercase text-white/50">
                    {t("find.email")}
                  </div>
                  <div className="mt-2 font-display text-xl md:text-2xl group-hover:text-gold transition-colors break-all">
                    {BRAND.email}
                  </div>
                </div>
              </a>

              <div
                data-testid="contact-phone"
                className="group p-8 md:p-10 bg-black hover:bg-[#0a0a0a] transition-colors flex flex-col gap-4"
              >
                <Phone size={18} className="text-gold" />
                <div>
                  <div className="text-[10px] tracking-[0.35em] uppercase text-white/50">
                    {t("find.phone")}
                  </div>
                  <a
                    data-testid="contact-phone-link"
                    href={BRAND.phoneHref}
                    className="mt-2 block font-display text-xl md:text-2xl hover:text-gold transition-colors"
                  >
                    {BRAND.phone}
                  </a>
                  <a
                    data-testid="contact-phone-ch"
                    href={BRAND.phoneCHHref}
                    className="mt-1 block font-display text-lg md:text-xl text-white/70 hover:text-gold transition-colors"
                  >
                    {BRAND.phoneCH}
                  </a>
                </div>
              </div>

              <div
                data-testid="contact-address"
                className="p-8 md:p-10 bg-black flex flex-col gap-4"
              >
                <MapPin size={18} className="text-gold" />
                <div>
                  <div className="text-[10px] tracking-[0.35em] uppercase text-white/50">
                    {t("find.address")}
                  </div>
                  <div className="mt-2 font-display text-xl md:text-2xl">
                    {BRAND.address[lang]}
                  </div>
                  <div className="mt-1 text-white/50 text-sm font-light">
                    {t("find.address.detail")}
                  </div>
                </div>
              </div>

              <div
                data-testid="contact-social"
                className="p-8 md:p-10 bg-black flex flex-col gap-4"
              >
                <div className="flex items-center gap-3 text-gold">
                  <Instagram size={16} />
                  <Facebook size={16} />
                  <TikTokIcon size={16} />
                </div>
                <div>
                  <div className="text-[10px] tracking-[0.35em] uppercase text-white/50">
                    {t("find.follow")}
                  </div>
                  <div className="mt-2 flex flex-col gap-1.5">
                    <a
                      data-testid="social-instagram"
                      href={BRAND.social.instagram.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-display text-base md:text-lg hover:text-gold transition-colors"
                    >
                      Instagram · {BRAND.social.instagram.handle}
                    </a>
                    <a
                      data-testid="social-tiktok"
                      href={BRAND.social.tiktok.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-display text-base md:text-lg hover:text-gold transition-colors"
                    >
                      TikTok · {BRAND.social.tiktok.handle}
                    </a>
                    <a
                      data-testid="social-facebook"
                      href={BRAND.social.facebook.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="font-display text-base md:text-lg hover:text-gold transition-colors"
                    >
                      Facebook · {BRAND.social.facebook.handle}
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-10%" }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          className="mt-20 md:mt-28 pt-16 md:pt-20 border-t border-gold/15"
        >
          <ContactForm />
        </motion.div>
      </div>
    </section>
  );
};

export default FindUs;
